<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import type { Detection } from '~/types'
import { useFileDialog } from '@vueuse/core'
import { computed, onUnmounted, ref, watch } from 'vue'
import { toast } from 'vue-sonner'
import PreviewBox from '~/components/PreviewBox.vue'
import { Button as TheButton } from '~/components/ui/button'
import { useAnimationFrameTask } from '~/composables/useAnimationFrameTask'
import { useFps } from '~/composables/useFps'
import { drawLetterboxedSource } from '~/utils/image'

const props = defineProps<{
  modelKey: string
  modelSize: number
  isActive: boolean
  detectImageData: (imageDataBuffer: ArrayBuffer, size: number) => Promise<{ detections: Detection[], _transfer: ArrayBuffer[] }>
  drawDetections: (ctx: CanvasRenderingContext2D, detections: Detection[]) => void
}>()

const videoRef = ref<HTMLVideoElement | null>(null)
const captureCanvasRef = ref<HTMLCanvasElement | null>(null)
const resultCanvasRef = ref<HTMLCanvasElement | null>(null)
const videoUrl = ref<string | null>(null)
const videoFileName = ref('')
const hasVideo = computed(() => videoUrl.value !== null)
const { fps, updateFps } = useFps()
let detectionGeneration = 0

const fileDialog = useFileDialog({
  accept: 'video/*',
  multiple: false,
})

async function renderDetectionFrame(generation: number) {
  const video = videoRef.value
  const captureCanvas = captureCanvasRef.value
  const resultCanvas = resultCanvasRef.value
  if (!video || !captureCanvas || !resultCanvas) {
    return
  }

  const captureCtx = captureCanvas.getContext('2d', { willReadFrequently: true })
  const resultCtx = resultCanvas.getContext('2d')
  if (!captureCtx || !resultCtx) {
    throw new Error('Failed to get video canvas context')
  }

  const modelSize = props.modelSize
  drawLetterboxedSource(captureCtx, video, video.videoWidth, video.videoHeight, modelSize)
  const imageData = captureCtx.getImageData(0, 0, modelSize, modelSize)
  const { detections, _transfer } = await props.detectImageData(imageData.data.buffer, modelSize)

  if (generation !== detectionGeneration || modelSize !== props.modelSize || video !== videoRef.value || captureCanvas !== captureCanvasRef.value || resultCanvas !== resultCanvasRef.value || !props.isActive || video.paused || video.ended) {
    return
  }

  resultCtx.putImageData(new ImageData(new Uint8ClampedArray(_transfer[0]), modelSize, modelSize), 0, 0)
  props.drawDetections(resultCtx, detections)
  updateFps()
  resultCtx.fillStyle = 'rgb(0, 255, 0)'
  resultCtx.font = '20px Arial'
  resultCtx.fillText(`Detection FPS: ${fps.value}`, 0, 20)
}

const detectTask = useAnimationFrameTask(async () => {
  const video = videoRef.value
  if (!video || !props.isActive || video.paused || video.ended) {
    return
  }

  const generation = detectionGeneration
  try {
    await renderDetectionFrame(generation)
  }
  catch (error) {
    if (generation !== detectionGeneration) {
      return
    }

    const errorMessage = error instanceof Error ? error.message : String(error)
    toast.error(`Failed to detect video frame: ${errorMessage}`)
    videoRef.value?.pause()
    throw error
  }
})

function startDetection() {
  const video = videoRef.value
  if (!video || !props.isActive || video.paused || video.ended) {
    return
  }

  detectTask.resume()
}

function stopDetection() {
  detectionGeneration++
  detectTask.pause()
}

function clearDetectionResult() {
  const canvas = resultCanvasRef.value
  const ctx = canvas?.getContext('2d')
  if (!canvas || !ctx) {
    return
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height)
}

function onVideoRef(el: Element | ComponentPublicInstance | null) {
  if (el instanceof HTMLVideoElement) {
    videoRef.value = el
    return
  }

  videoRef.value = null
}

function onResultCanvasRef(el: Element | ComponentPublicInstance | null) {
  if (el instanceof HTMLCanvasElement) {
    resultCanvasRef.value = el
    return
  }

  resultCanvasRef.value = null
}

function onCaptureCanvasRef(el: Element | ComponentPublicInstance | null) {
  if (el instanceof HTMLCanvasElement) {
    captureCanvasRef.value = el
    return
  }

  captureCanvasRef.value = null
}

function revokeVideoUrl() {
  if (!videoUrl.value) {
    return
  }

  URL.revokeObjectURL(videoUrl.value)
  videoUrl.value = null
}

fileDialog.onChange((files) => {
  if (!files) {
    return
  }

  const file = files.item(0)
  if (!file) {
    return
  }

  stopDetection()
  videoRef.value?.pause()
  revokeVideoUrl()
  clearDetectionResult()
  videoFileName.value = file.name
  videoUrl.value = URL.createObjectURL(file)
  fileDialog.reset()
})

watch(() => props.isActive, (active) => {
  if (active) {
    startDetection()
    return
  }

  videoRef.value?.pause()
  stopDetection()
})

watch(() => props.modelKey, () => {
  videoRef.value?.pause()
  stopDetection()
  clearDetectionResult()
})

onUnmounted(() => {
  stopDetection()
  revokeVideoUrl()
})
</script>

<template>
  <div class="flex flex-col gap-2 mb-2 sm:flex-row sm:items-center">
    <TheButton type="button" variant="outline" @click="fileDialog.open()">
      <span i-lucide-video data-icon="inline-start" />
      Select video
    </TheButton>
    <div class="max-w-120 truncate text-sm text-gray-500">
      {{ videoFileName || 'Choose a local video to start detection.' }}
    </div>
  </div>

  <canvas
    :ref="onCaptureCanvasRef"
    :width="modelSize"
    :height="modelSize"
    class="hidden"
    aria-hidden="true"
  />

  <div class="flex flex-col gap-2 lg:flex-row">
    <PreviewBox
      w="80 md:120 lg:160"
      h="80 md:120 lg:160"
      :show-placeholder="!hasVideo"
    >
      <video
        :ref="onVideoRef"
        :src="videoUrl ?? undefined"
        class="size-full object-contain"
        controls
        playsinline
        @play="startDetection"
        @pause="stopDetection"
        @ended="stopDetection"
      />
      <template #placeholder>
        Local video preview
      </template>
    </PreviewBox>

    <PreviewBox
      w="80 md:120 lg:160"
      h="80 md:120 lg:160"
      :show-placeholder="!hasVideo"
    >
      <canvas
        :ref="onResultCanvasRef"
        :width="modelSize"
        :height="modelSize"
        class="origin-top-left"
        scale="50 md:75 lg:100"
        transition-all duration-300
      />
      <template #placeholder>
        Detection result
      </template>
    </PreviewBox>
  </div>
</template>
